'use client';

import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import posthog from 'posthog-js';
import { Button } from '../ui/button';
import { env } from '@/env';
import { useHasProPlan } from '@/hooks/useHasProPlan';
import { useCreateCheckoutSession, useCreatePortalSession } from '@/lib/api/billing';

export const ProCTA = ({
	locale,
	isAuthenticated,
	planPeriod,
}: {
	locale: string;
	isAuthenticated: boolean;
	planPeriod: 'month' | 'annual';
}) => {
	const t = useTranslations('plans');
	const { hasProPlan, isCanceled } = useHasProPlan();
	const createCheckoutSession = useCreateCheckoutSession();
	const createPortalSession = useCreatePortalSession();

	const priceId =
		planPeriod === 'annual'
			? env.NEXT_PUBLIC_STRIPE_PRO_PRICE_ID_ANNUAL
			: env.NEXT_PUBLIC_STRIPE_PRO_PRICE_ID_MONTHLY;

	const oidcIssuer = process.env.NEXT_PUBLIC_OIDC_ISSUER_URL;
	const oidcClientId = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID || 'qrcodly';
	const frontendUrl = process.env.NEXT_PUBLIC_FRONTEND_URL || 'https://qr.dslan.at';
	const signInHref = oidcIssuer
		? `${oidcIssuer}/protocol/openid-connect/auth?client_id=${oidcClientId}&redirect_uri=${encodeURIComponent(`${frontendUrl}/api/auth/callback/oidc`)}&response_type=code&scope=openid%20profile%20email`
		: '/dashboard';

	const handleUpgrade = () => {
		posthog.capture('subscription:checkout_started', { source: 'plans_page', period: planPeriod });
		createCheckoutSession.mutate({ priceId, locale });
	};

	if (!isAuthenticated) {
		return (
			<Link href={signInHref}>
				<Button variant="secondary">{t('upgradeToPro')}</Button>
			</Link>
		);
	}

	if (isCanceled) {
		return (
			<Button
				variant="secondary"
				onClick={() => createPortalSession.mutate({ locale })}
				disabled={createPortalSession.isPending}
			>
				{t('renewSubscription')}
			</Button>
		);
	}

	if (hasProPlan || isAuthenticated) {
		return (
			<Link href="/dashboard/qr-codes">
				<Button variant="secondary">Go to Dashboard</Button>
			</Link>
		);
	}

	return (
		<Button
			variant="secondary"
			onClick={handleUpgrade}
			disabled={createCheckoutSession.isPending}
		>
			{t('upgradeToPro')}
		</Button>
	);
};
